import { Drawer as DrawerAnt } from 'antd';
import classNames from 'classnames';
import React, { createContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePortalZIndex } from '../../hooks/usePortalZIndex';
import { ButtonRounded } from '../buttonRounded/buttonRounded.component';
import { Spinner } from '../spinner/spinner.component';
import { Text } from '../text/text.component';
import './drawer.component.scss';
import { Tab, TabGroup } from '../tab/tab.component';

export const DrawerContext = createContext<{
  onClose: () => void;
  onOpen: () => void;
} | null>(null);

export declare namespace DrawerType {
  type Props = {
    className?: string;
    children?: React.ReactNode;
    triggerElement?: React.ReactNode;
    onClose?: () => void;
    placement?: 'top' | 'left' | 'right' | 'bottom';
    size?: 'small' | 'medium' | 'large' | 'full';
    isOpen?: boolean;
    title?: string | null;
    subTitle?: string | null;
    description?: string | null;
    extra?: React.ReactNode[];
    maskClosable?: boolean;
    isLoading?: boolean | null;
    enableButtonClose?: boolean | null;
    keyboard?: boolean;
    tab?: {
      options: Array<{
        label: string;
        value: string;
      }>;
      onChange: (value: string) => void;
      value: string;
    };
  };
}

export const Drawer = ({
  className,
  children,
  triggerElement,
  onClose,
  placement = 'right',
  size = 'medium',
  isOpen,
  title,
  subTitle,
  description,
  extra,
  maskClosable = true,
  isLoading = false,
  enableButtonClose = true,
  tab,
}: DrawerType.Props) => {
  const [open, setOpen] = useState<boolean>(isOpen ?? false);
  const zIndex = usePortalZIndex(open);

  useEffect(() => {
    typeof isOpen === 'boolean' && setOpen(isOpen);
  }, [isOpen, setOpen]);

  const drawerContent = zIndex ? (
    <DrawerAnt
      rootClassName={classNames('drawer', `drawer--size--${size}`, className)}
      placement={placement}
      size={'large'}
      onClose={() => {
        setOpen(false);
        onClose?.();
      }}
      maskClosable={maskClosable}
      destroyOnClose={true}
      keyboard={false}
      open={open ?? false}
      getContainer={false}
      rootStyle={{ zIndex: zIndex ?? undefined }}
      style={{ zIndex: zIndex ?? undefined }}
      extra={
        <div className="drawer__extra">
          <div className="drawer__extra__presentation">
            {subTitle && (
              <Text variant="label" color="black_90">
                {subTitle}
              </Text>
            )}
            {title && (
              <Text variant="title-1" color="black_90">
                {title}
              </Text>
            )}
            {description && (
              <div className="drawer__extra__presentation__description">
                <Text variant="title-3" color="black_90">
                  {description}
                </Text>
              </div>
            )}
          </div>
          <div className="drawer__extra__actions">
            {extra &&
              React.Children.map(extra, (item, index) => (
                <div key={index}>{item}</div>
              ))}

            {enableButtonClose && (
              <ButtonRounded
                onClick={() => {
                  setOpen(false);
                  onClose?.();
                }}
                icon="close"
                variant="primary"
              />
            )}
          </div>
        </div>
      }
    >
      <div
        className="drawer__content"
        style={{ gridTemplateRows: tab ? 'min-content 1fr' : '1fr' }}
      >
        {tab && (
          <TabGroup>
            {tab.options.map((option) => (
              <Tab
                key={option.value}
                selected={option.value === tab.value}
                onClick={() => tab.onChange(option.value)}
              >
                {option.label}
              </Tab>
            ))}
          </TabGroup>
        )}
        <div className="drawer__content__children">
          {isLoading && (
            <div className="drawer__content__children__loading">
              <Spinner />
            </div>
          )}

          {children}
        </div>
      </div>
    </DrawerAnt>
  ) : null;

  return (
    <DrawerContext.Provider
      value={{
        onOpen: () => setOpen(true),
        onClose: () => {
          setOpen(false);
          onClose?.();
        },
      }}
    >
      {children && (
        <div onClick={() => setOpen(true)} className="drawertrigger">
          {triggerElement}
        </div>
      )}
      {open && zIndex
        ? createPortal(
            <div
              className="drawer-portal-wrapper"
              style={{ zIndex: zIndex ?? undefined }}
            >
              {drawerContent}
            </div>,
            document.body,
          )
        : null}
    </DrawerContext.Provider>
  );
};
